import React from 'react';

import { captureException } from '@sentry/react';
import { EditorState, getDefaultKeyBinding, KeyBindingUtil, Modifier } from 'draft-js';
import 'draft-js-emoji-plugin/lib/plugin.css';
import createLinkifyPlugin from 'draft-js-linkify-plugin';
import 'draft-js-linkify-plugin/lib/plugin.css';
import createMentionPlugin, { defaultSuggestionsFilter } from 'draft-js-mention-plugin';
import Editor from 'draft-js-plugins-editor';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { wordBreakCSS, HideScrollbarCSS, EmojiFontCSS } from 'styles/common';
import { debounce, throttle } from 'throttle-debounce';
import { checkFeedbackModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  HASHTAG_MENTION_REGEX,
  POST_MODAL_COMPETENCY_NOT_APPLICABLE_ERROR,
  SEARCH_WIDGET_TYPES,
  TASK_TYPES_2,
} from 'constants/ProjectConstants';

import { getCompetenciesForEmployee } from 'services/admin';
import { getEntities, checkIfMentionWasRemoved, getEntitiesByLogic, replaceEntityText } from 'services/helpers/post';
import { employeeAutocompleteForMention, tagAutocomplete, driversAutocomplete } from 'services/search';
import { showErrorSnackbar } from 'services/snackbar';
import { createTag } from 'services/tag';

import DriverEntryComponent from './DriverEntryComponent';
import './EmojiStyleSize.css';
import employeeMentionStyles from './EmployeeMentionStyles.module.css';
import hashTagMentionStyles from './HashtagMentionStyles.module.css';
import linkStyles from './LinkStyles.module.css';
import MentionEntryComponent from './MentionEntryComponent';
import mentionStyles from './MentionStyles.module.css';
import TagEntryComponent from './TagEntryComponent';
import { createNewEmojiPlugin } from './helper';
import { getDecoratorPlugin } from './initialEditorState';
import { positionSuggestions } from './positionSuggestion';
import { money } from './remoteData';

const { hasCommandModifier } = KeyBindingUtil;

const ENTER_COMMAND = 'onEnter';
const ESCAPE_COMMAND = 'onEscape';

function myKeyBindingFn(e, allowNewLine) {
  e.stopPropagation();
  // if enter is pressed and new line is not allowed, consider it an enter command
  if (e.keyCode === 13 && !allowNewLine) return ENTER_COMMAND;
  // if enter is pressed and ctrl or cmd is not pressed, consider it an enter command
  if (e.keyCode === 13 && !hasCommandModifier(e) /* `Enter Key` key // && hasCommandModifier(e) */) {
    return ENTER_COMMAND;
  }
  if (e.keyCode === 27) {
    return ESCAPE_COMMAND;
  }
  return getDefaultKeyBinding(e);
}

function customSuggestionsFilter(searchValue, suggestions) {
  const size = (list) => (list.constructor.name === 'List' ? list.size : list.length);

  const get = (obj, attr) => (obj.get ? obj.get(attr) : obj[attr]);

  const value = searchValue.toLowerCase();
  const filteredSuggestions = suggestions.filter(
    (suggestion) => !value || get(suggestion, 'name').toLowerCase().indexOf(value) > -1
  );
  const length = size(filteredSuggestions) < 10 ? size(filteredSuggestions) : 10;
  return filteredSuggestions.slice(0, length);
}

/*
  both employee search and hashtag search are dependent on teamId
*/
class PostField extends React.Component {
  constructor(props) {
    super(props);
    this.employeeMentionPlugin = createMentionPlugin({
      positionSuggestions: this.props.positionSuggestions || positionSuggestions,
      mentionPrefix: '@',
      mentionTrigger: '@',
      entityMutability: 'IMMUTABLE',
      supportWhitespace: true,
      theme: { ...mentionStyles, ...employeeMentionStyles },
      popperOptions: this.props.popperOptions ?? {},
    });
    this.hashTagMentionPlugin = createMentionPlugin({
      positionSuggestions: this.props.positionSuggestions || positionSuggestions,
      mentionPrefix: '#',
      mentionTrigger: '#',
      entityMutability: 'IMMUTABLE',
      mentionRegExp: HASHTAG_MENTION_REGEX,
      theme: { ...mentionStyles, ...hashTagMentionStyles },
      popperOptions: this.props.popperOptions ?? {},
    });

    this.emojiPlugin = createNewEmojiPlugin({
      size: 'small',
      iconSize: this.props.emojiPluginSize,
    });

    // this.plusMentionPlugin = createMentionPlugin({
    //   mentionPrefix: '+',
    //   mentionTrigger: '+',
    //   // entityMutability: 'IMMUTABLE',
    //   theme: { ...mentionStyles, ...employeeMentionStyles }
    // });
    // this.hashtagPlugin = createHashtagPlugin({
    //   theme: hashTagMentionStyles
    // });
    this.plusPlugin = getDecoratorPlugin();
    this.linkifyPlugin = createLinkifyPlugin({ target: '_blank', theme: linkStyles });
    this.plugins = [];
    if (this.props.useMentionFunctionality) this.plugins.push(this.employeeMentionPlugin);
    if (this.isHashtagFunctionalityEnabled() || this.props.useHashtagDriverFunctionality)
      this.plugins.push(this.hashTagMentionPlugin);
    if (this.props.useMoneyFunctionality && checkFeedbackModuleEnabled()) this.plugins.push(this.plusPlugin);
    if (this.props.emojiPlugin) this.plugins.push(this.emojiPlugin);
    this.plugins = [...this.plugins, this.linkifyPlugin];
    this.editor = null;
    this.throttledCompetencyValidateCall = null;
  }

  componentDidCatch(err, errorInfo) {
    // Don't remove this console errors, these are used so that sentry shows them in its logs
    console.error('error ===', err);
    console.error('error info === ', errorInfo);
    captureException('Postfield error caught');
    if (this.editor && this.editor.forceUpdate) this.editor.forceUpdate();
  }

  state = {
    suggestions: [],
    tagsSuggestions: [],
    moneySuggestions: [],
    driversSuggestions: [],
    loadingApplicableCompetencies: false,
    hasError: false,
    errorMessage: null,
  };

  employeeMentionCall = ({ value, teamId, widgetType }) => {
    employeeAutocompleteForMention({ query: value, teamId, widgetType })
      .then((employees) => {
        let filterEmployees = [];
        if (this.props.mentionEmpIds.length > 0) {
          filterEmployees = employees.filter(
            ({ employee }) => !!this.props.mentionEmpIds.find((id) => id == employee.id)
          );
        } else {
          filterEmployees = employees;
        }
        this.setState({
          suggestions: defaultSuggestionsFilter(value, filterEmployees),
        });
      })
      .catch((err) => this.setState({ suggestions: [] }));
  };

  getAllReplaceableEntities = (editorState) =>
    getEntitiesByLogic({
      editorState,
      findEntity: ({ type }) => type === '#mention' || type === 'mention',
    });

  replaceTextOfAllEntities = (editorState, body) => {
    const entities = this.getAllReplaceableEntities(editorState);
    entities.forEach((entity) => {
      editorState = replaceEntityText(editorState, entity, body);
    });
    return editorState;
  };

  // debouncedEmployeeMentionCall = debounce(300, this.employeeMentionCall);
  tagCall = ({ value, teamId, widgetType }) => {
    const mentionedEmployeeIds = this.props.mentionedEmployeeIds ?? [];
    const body = {
      macros: {
        employees: {},
        demographics: {},
        attributes: { orgTag: {}, money: null },
      },
    };
    this.replaceTextOfAllEntities(this.props.editorState, body);
    const employeeIds = Object.keys(body.macros.employees)?.length ? Object.keys(body.macros.employees) : [];

    const employeesMentioned = [...employeeIds, ...mentionedEmployeeIds];
    // removed {teamId} for the hashtag autocomplete
    tagAutocomplete({
      query: value,
      teamId: null,
      widgetType,
      employeeIds: employeesMentioned?.length ? employeesMentioned : null,
    })
      .then((tags) => {
        const suggestions = customSuggestionsFilter(value, tags);
        teamId = teamId || this.props.preferredTeamId;
        if (this.props.createNewTagFunctionality && suggestions.length === 0 && teamId && value)
          suggestions.push({
            key: 'createNewTag',
            uuid: 'createNewTag',
            name: value,
            displayName: 'Create New Tag',
            teamId,
            onAddTag: this.debouncedTagCall,
          });
        this.setState({
          tagsSuggestions: suggestions,
        });
      })
      .catch((err) => this.setState({ tagsSuggestions: [] }));
  };

  driverCall = ({ value, widgetType }) => {
    driversAutocomplete({ query: value, widgetType })
      .then((drivers) => {
        const suggestions = customSuggestionsFilter(value, drivers);
        this.setState({
          driversSuggestions: suggestions,
        });
      })
      .catch((err) => this.setState({ driversSuggestions: [] }));
  };

  fetchApplicableCompetencies = (employeeIds) => {
    this.setState({ loadingApplicableCompetencies: true });
    return getCompetenciesForEmployee({
      employeeIds,
    })
      .then((res) => res)
      .catch((err) => console.error('Error fetching applicable competencies', err))
      .finally(() => {
        this.setState({ loadingApplicableCompetencies: false });
      });
  };

  validateMentionedCompetencies = throttle(500, (editorState) => {
    const body = {
      macros: {
        employees: {},
        demographics: {},
        attributes: { orgTag: {}, money: null },
      },
    };
    this.replaceTextOfAllEntities(editorState, body);

    const mentionedOrgTagIds = Object.keys(body.macros.attributes.orgTag);
    const mentionedEmployeeIds = Object.keys(body.macros.employees);

    if (mentionedOrgTagIds.length > 0 && mentionedEmployeeIds.length > 0) {
      this.fetchApplicableCompetencies(mentionedEmployeeIds).then((res) => {
        const mentionedCompetenciesAreApplicable = mentionedOrgTagIds.every((v) => res?.includes(+v));
        if (mentionedCompetenciesAreApplicable) {
          if (
            this.props.setErrorMessage &&
            this.props.setHasError &&
            typeof this.props.setErrorMessage === 'function' &&
            typeof this.props.setHasError === 'function'
          ) {
            this.props.setHasError(false);
            this.props.setErrorMessage(null);
          }

          this.setState({
            hasError: false,
            errorMessage: null,
          });
        } else {
          const errorMessage = POST_MODAL_COMPETENCY_NOT_APPLICABLE_ERROR;

          // checking if props have setHasError and setErrorMessage functions

          if (
            this.props.setErrorMessage &&
            this.props.setHasError &&
            typeof this.props.setErrorMessage === 'function' &&
            typeof this.props.setHasError === 'function'
          ) {
            this.props.setHasError(true);
            this.props.setErrorMessage(errorMessage);
          }

          this.setState({
            hasError: true,
            errorMessage,
          });
        }
      });
    } else if (
      this.state.hasError &&
      typeof this.props.setErrorMessage === 'function' &&
      typeof this.props.setHasError === 'function'
    ) {
      this.props.setHasError(false);
      this.props.setErrorMessage(null);
      this.setState({
        hasError: false,
        errorMessage: null,
      });
    }
  });

  componentDidMount() {
    if (this.props.setEmojiPlugin) {
      this.props.setEmojiPlugin(this.emojiPlugin);
    }

    if (!this.props.focusOnMount) {
      return;
    }

    // Doing this without timeout stops all mention plugins
    // timeout can also cause errors
    setTimeout(() => {
      if (this.editor) {
        this.editor.focus();
      }
    }, 100);
  }

  // I didn't understand but draft js sends some plugin props to onChange method which never get used
  onChange = (editorState, someProps, selectionState = null) => {
    if (this.props.onRemoveMention) {
      const lastChangeType = editorState.getLastChangeType();
      if (lastChangeType === 'backspace-character' || lastChangeType === 'remove-range') {
        const removed = checkIfMentionWasRemoved({
          oldEditorState: this.props.editorState,
          newEditorState: editorState,
        });
        if (removed) this.props.onRemoveMention(removed);
      }
    }
    this.props.updateEditorState(editorState);
    // otherwise cursor shifts to the wrong place on adding tag
    if (selectionState) {
      setTimeout(() => {
        this.props.updateEditorState(EditorState.forceSelection(this.props.editorState, selectionState));
      }, 10);
    }
    this.validateMentionedCompetencies(
      selectionState ? EditorState.forceSelection(this.props.editorState, selectionState) : editorState
    );
  };

  // these widget types are being checked because they contain competency(Feedback Module) feature
  isHashtagFunctionalityEnabled = () => {
    if (this.props.useHashtagFunctionality) {
      if (['POST', 'COMMENT'].includes(this.props.widgetType)) {
        return checkFeedbackModuleEnabled();
      }
      return true;
    }
    return false;
  };

  onSearchChange = ({ value, ...props }) => {
    if (!this.debouncedEmployeeMentionCall) this.debouncedEmployeeMentionCall = debounce(300, this.employeeMentionCall);

    this.debouncedEmployeeMentionCall({
      value,
      teamId: this.props.teamId,
      widgetType: this.props.widgetType,
    });
  };

  onHashSearchChange = ({ value }) => {
    if (!this.debouncedTagCall) this.debouncedTagCall = debounce(300, this.tagCall);
    this.debouncedTagCall({ value, teamId: this.props.teamId, widgetType: this.props.widgetType });
  };

  onHashDriverSearchChange = ({ value }) => {
    if (!this.debouncedDriverCall) this.debouncedDriverCall = debounce(300, this.driverCall);
    this.debouncedDriverCall({ value, widgetType: this.props.widgetType });
  };

  onMoneySearchChange = ({ value }) => {
    this.setState({
      moneySuggestions: defaultSuggestionsFilter(value, money),
    });
  };

  handleKeyCommand = (command) => {
    if (command === ENTER_COMMAND) {
      if (!this.state.hasError) {
        if (this.props.onEnter) this.props.onEnter();
      } else {
        this.props.dispatchErrorSnackbar(this.state.errorMessage);
      }

      return 'handled';
    }
    if (command === ESCAPE_COMMAND) {
      if (this.props.onEscape) this.props.onEscape();
      return 'handled';
    }
    return 'not-handled';
  };

  onAddMention = (mention) => {
    this.setState({ suggestions: [] });
    if (typeof this.props.onAddMention === 'function') this.props.onAddMention(mention);
  };

  // a hashtag was added, now removing the suggestions
  // otherwise the dropdown would keep opening because that's
  // how the library has been coded
  onAddHashtagMention = (mention) => {
    const { key, teamId, name } = mention;
    if (key === 'createNewTag') {
      if (typeof this.props.beforeCreateNewHashtag === 'function') this.props.beforeCreateNewHashtag();
      let newTeamId = teamId;
      let tagType = 'MESH_TASK';
      if (this.props.widgetType === TASK_TYPES_2.goal) {
        tagType = 'GOAL';
        newTeamId = null;
      }
      createTag({ name, displayName: name, teamId: newTeamId, tagType })
        .then((tag) => {
          const entities = getEntities({
            editorState: this.props.editorState,
            entityType: '#mention',
            name,
          });
          if (entities.length === 0) {
            return;
          }
          const selectionState = this.props.editorState.getSelection();

          const contentState = this.props.editorState.getCurrentContent();
          // const selectionAfter = contentState.getSelectionAfter();
          const newContentState = contentState.replaceEntityData(entities[0].entityKey, {
            mention: {
              id: tag.id, // TODO: Ideally we should be using either id or uuid, not both
              uuid: tag.id,
              name: tag.name,
              displayName: tag.displayName,
            },
          });
          // newContentState = Modifier.insertText(newContentState, selectionAfter, '');
          const newState = EditorState.createWithContent(newContentState);
          this.onChange(newState, null, selectionState);
        })
        .catch((err) => {
          this.props.dispatchErrorSnackbar('Sorry, an error occurred while creating project');
          const contentState = this.props.editorState.getCurrentContent();
          const entities = getEntities({
            editorState: this.props.editorState,
            entityType: '#mention',
            name,
          });
          if (entities.length === 0) {
            return;
          }
          const selectionState = this.props.editorState.getSelection();
          const entitySelection = selectionState.merge({
            anchorOffset: entities[0].start,
            focusOffset: entities[0].end,
          });
          const newContentState = Modifier.applyEntity(contentState, entitySelection, null);
          const newEditorState = EditorState.createWithContent(newContentState);
          // const newEditorState = EditorState.push(
          //   this.props.editorState,
          //   newContentState,
          //   'apply-entity'
          // );
          this.onChange(newEditorState, null, selectionState);
          // dispatch(showErrorSnackbar('Sorry, an error occured'));
        })
        .finally(() => {
          // setLoading(false);
          // parentProps.onMouseUp(e);
        });
    }
    this.setState({
      tagsSuggestions: [],
    });
  };

  // a hashtag was added, now removing the suggestions
  // otherwise the dropdown would keep opening because that's
  // how the library has been coded
  onAddHashtagDriverMention = () => {
    this.setState({
      driversSuggestions: [],
    });
  };

  focusMentionOnKeypress = (e, keyBindingFn) => {
    if (!keyBindingFn) return;
    keyBindingFn(e);
    const focusedSuggestion = document.getElementsByClassName('mentionSuggestionEntryFocused')[0];
    focusedSuggestion &&
      focusedSuggestion.scrollIntoView({
        behavior: 'smooth',
        // block: 'center'
      });
  };

  mentionSuggestionsOpenListener = (e) => {
    this.focusMentionOnKeypress(e, this.employeeMentionPlugin.keyBindingFn);
  };

  tagSuggestionsOpenListener = (e) => {
    this.focusMentionOnKeypress(e, this.hashTagMentionPlugin.keyBindingFn);
  };

  driverSuggestionsOpenListener = (e) => {
    this.focusMentionOnKeypress(e, this.hashTagMentionPlugin.keyBindingFn);
  };

  onMentionSuggestionsOpen = () => {
    document.addEventListener('keydown', this.mentionSuggestionsOpenListener);
  };

  onTagSuggestionsOpen = () => {
    document.addEventListener('keydown', this.tagSuggestionsOpenListener);
  };

  onDriverSuggestionsOpen = () => {
    document.addEventListener('keydown', this.driverSuggestionsOpenListener);
  };

  onTagSuggestionsClose = () => {
    // Cancelling calls in the queue as the dropdown has been closed
    this.debouncedTagCall && typeof this.debouncedTagCall.cancel === 'function' && this.debouncedTagCall.cancel();
    // setting it to null, so that we know in search change that we need to set it again
    this.debouncedTagCall = null;
    this.setState({
      tagsSuggestions: [],
    });
    document.removeEventListener('keydown', this.tagSuggestionsOpenListener);
  };

  onDriverSuggestionsClose = () => {
    // Cancelling calls in the queue as the dropdown has been closed
    this.debouncedDriverCall &&
      typeof this.debouncedDriverCall.cancel === 'function' &&
      this.debouncedDriverCall.cancel();
    // setting it to null, so that we know in search change that we need to set it again
    this.debouncedDriverCall = null;
    this.setState({
      driversSuggestions: [],
    });
    document.removeEventListener('keydown', this.driverSuggestionsOpenListener);
  };

  onMentionSuggestionsClose = () => {
    // Cancelling calls in the queue as the dropdown has been closed
    this.debouncedEmployeeMentionCall &&
      typeof this.debouncedEmployeeMentionCall.cancel === 'function' &&
      this.debouncedEmployeeMentionCall.cancel();
    // setting it to null, so that we know in search change that we need to set it again
    this.debouncedEmployeeMentionCall = null;
    this.setState({
      suggestions: [],
    });
    // this.debouncedEmployeeMentionCall = debounce(300, this.employeeMentionCall);
    document.removeEventListener('keydown', this.mentionSuggestionsOpenListener);
  };

  focus = () => {
    if (this.editor) this.editor.focus();
  };

  render() {
    const { MentionSuggestions } = this.employeeMentionPlugin;
    const { MentionSuggestions: HashTagSuggestions } = this.hashTagMentionPlugin;
    const { MentionSuggestions: HashTagDriverSuggestions } = this.hashTagMentionPlugin;
    //
    // const { MentionSuggestions: PlusSuggestions } = this.plusMentionPlugin;
    return this.props.editorState ? (
      <PostFieldContainer
        className={`${this.props.className} containerStyles`} // this is a better way to do it, rather than using containerstyles
        containerStyles={this.props.containerStyles}
        editorStyles={this.props.editorStyles}
        placeholderStyles={this.props.placeholderStyles}
        height={this.props.height}
        onClick={this.focus}
      >
        <Editor
          editorState={this.props.editorState}
          onChange={this.onChange}
          plugins={this.plugins}
          placeholder={this.props.placeholder || 'What are you thinking about?'}
          ref={(element) => {
            this.editor = element;
          }}
          onfocus={this.props.handleFocus}
          onBlur={(e) => {
            if (this.props.onBlurOverride) {
              return this.props?.handleBlur?.(e);
            }
            if (this.props.editorState.getCurrentContent().hasText()) {
              return this.props?.handleBlur?.(e);
            }
          }}
          handleKeyCommand={this.handleKeyCommand}
          keyBindingFn={(e) => myKeyBindingFn(e, this.props.allowNewLine)}
          readOnly={this.props.readOnly}
        />
        {/* both tag and driver suggestions use #, so enable only either of these */}
        {this.isHashtagFunctionalityEnabled() && !this.props.useHashtagDriverFunctionality && (
          <HashTagSuggestions
            onSearchChange={this.onHashSearchChange}
            suggestions={this.state.tagsSuggestions}
            onAddMention={this.onAddHashtagMention}
            entryComponent={TagEntryComponent}
            onOpen={this.onTagSuggestionsOpen}
            onClose={this.onTagSuggestionsClose}
          />
        )}
        {this.props.useHashtagDriverFunctionality && !this.isHashtagFunctionalityEnabled() && (
          <HashTagDriverSuggestions
            onSearchChange={this.onHashDriverSearchChange}
            suggestions={this.state.driversSuggestions}
            onAddMention={this.onAddHashtagDriverMention}
            entryComponent={DriverEntryComponent}
            onOpen={this.onDriversSuggestionsOpen}
            onClose={this.onDriverSuggestionsClose}
          />
        )}
        {/* <this.EmojiSuggestions /> */}
        {/* <PlusSuggestions
          positionSuggestions={props => }
          onSearchChange={this.onMoneySearchChange}
          suggestions={this.state.moneySuggestions}
          onAddMention={this.onAddMention}
        /> */}
        {this.props.useMentionFunctionality && (
          <MentionSuggestions
            // positionSuggestions={props => }
            onSearchChange={this.onSearchChange}
            suggestions={this.state.suggestions}
            onAddMention={this.onAddMention}
            entryComponent={MentionEntryComponent}
            onOpen={this.onMentionSuggestionsOpen}
            onClose={this.onMentionSuggestionsClose}
          />
        )}
        {this.props.imageUploadComponent}
        {/* <this.EmojiSelect /> */}
      </PostFieldContainer>
    ) : null;
  }
}

const DefaultEditorStyles = css`
  font-size: 14px;
  width: 100%;
  border: 1px solid #d5d4d4;
  padding: ${(props) => props.theme.spacing(2)}rem ${(props) => props.theme.spacing(1.5)}rem;

  &:focus {
    border: 1px solid ${(props) => props.theme.palette.primary.main};
  }
  background: white;
  line-height: 1.5rem;
  ${(props) => css`
    min-height: ${props.height || '5vh'};
  `}
  color: ${(props) => props.theme.palette.text.primary};
  overflow-y: scroll;
  ${wordBreakCSS};
  font-family: ${(props) => props.theme.typography.fontFamily};
  span[title^=':'] {
    ${EmojiFontCSS};
  }
`;

const DefaultPlaceholderStyles = css`
  font-size: 14px;
  position: absolute;
  color: ${(props) => props.theme.palette.text.label};
  pointer-events: none;
  margin: ${(props) => props.theme.spacing(2.15)}rem ${(props) => props.theme.spacing(1.5)}rem;
  font-family: ${(props) => props.theme.typography.fontFamily};
`;

const PostFieldContainer = styled.div`
  position: relative;
  width: 100%;
  ${
    '' /* ${props =>
    css`
      min-height: ${props.height || '5vh'};
    `} */
  }
  .DraftEditor-editorContainer {
    ${DefaultEditorStyles};
    ${(props) => props.editorStyles};
    ${HideScrollbarCSS};
  }
  .mention {
    color: #4a85bb;
    text-decoration: none;
  }
  .public-DraftEditorPlaceholder-inner {
    ${DefaultPlaceholderStyles};
    ${(props) => props.placeholderStyles};
  }
`;

PostField.propTypes = {
  editorStyles: PropTypes.array,
  placeholderStyles: PropTypes.array,
  handleBlur: PropTypes.func,
  editorState: PropTypes.objectOf(EditorState).isRequired,
  updateEditorState: PropTypes.func.isRequired,
  positionSuggestions: PropTypes.func,
  preferredTeamId: PropTypes.string,
  beforeCreateNewHashtag: PropTypes.func,
  onAddMention: PropTypes.func,
  onRemoveMention: PropTypes.func,
  className: PropTypes.string,
  createNewTagFunctionality: PropTypes.bool,
  focusOnMount: PropTypes.bool,
  popperOptions: PropTypes.object,
};

PostField.defaultProps = {
  editorStyles: css``,
  placeholderStyles: css``,
  containerStyles: css``,
  useMentionFunctionality: true,
  mentionEmpIds: [],
  readOnly: false,
  useMoneyFunctionality: false,
  useHashtagFunctionality: true,
  useHashtagDriverFunctionality: false,
  className: '',
  widgetType: SEARCH_WIDGET_TYPES.search,
  createNewTagFunctionality: true,
  allowNewLine: false,
  focusOnMount: true,
  popperOptions: {},
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  dispatchErrorSnackbar: (message) => {
    dispatch(showErrorSnackbar(message));
  },
});

export default connect(null, mapDispatchToProps)(PostField);
